import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { realTimeDb } from '../firebase';
import { ref, get, push, runTransaction, update, set } from 'firebase/database';
import './FormViewer.css';
import { PLAN_CONFIG, DEFAULT_PLAN, getPlanLimit, isUnlimitedLimit, formatLimitLabel } from '../config/planConfig';

const LABEL_MAP_KEY = '__labelMap';
const FIREBASE_INVALID_CHARS = ['.', '#', '$', '/', '[', ']'];

const dedupeFieldsByLabel = (fieldGroups = []) => {
  const seen = new Map();
  const ordered = [];

  fieldGroups.forEach((group) => {
    (Array.isArray(group) ? group : []).forEach((field) => {
      if (!field || !field.label) {
        return;
      }
      const key = field.label.toString().trim().toLowerCase();
      if (!seen.has(key)) {
        const clone = { ...field };
        seen.set(key, clone);
        ordered.push(clone);
      }
    });
  });

  return ordered;
};

const collectFieldsForSelection = (selection, variants = [], baseFields = []) => {
  if (selection === 'all') {
    const buckets = [];
    if (Array.isArray(baseFields) && baseFields.length) {
      buckets.push(baseFields);
    }
    variants.forEach((variant) => {
      if (Array.isArray(variant?.fields) && variant.fields.length) {
        buckets.push(variant.fields);
      }
    });
    return dedupeFieldsByLabel(buckets);
  }

  const targetVariant = variants.find((variant) => variant.id === selection);
  if (targetVariant && Array.isArray(targetVariant.fields)) {
    return targetVariant.fields;
  }

  return Array.isArray(baseFields) ? baseFields : [];
};

const sanitiseKeyForFirebase = (rawKey) => {
  if (rawKey === undefined || rawKey === null) {
    return '';
  }

  const trimmed = rawKey.toString().trim();
  if (!trimmed) {
    return '';
  }

  let sanitised = '';
  for (const char of trimmed) {
    sanitised += FIREBASE_INVALID_CHARS.includes(char) ? '_' : char;
  }

  return sanitised;
};

const ensureUniqueKey = (baseKey, target, fallbackPrefix) => {
  let uniqueKey = baseKey || `${fallbackPrefix}_${Object.keys(target).length + 1}`;
  let suffix = 1;

  while (uniqueKey === LABEL_MAP_KEY || Object.prototype.hasOwnProperty.call(target, uniqueKey)) {
    uniqueKey = `${baseKey || fallbackPrefix}_${suffix}`;
    suffix += 1;
  }

  return uniqueKey;
};

const buildSanitisedSubmissionPayload = (rawData = {}) => {
  const payload = {};
  const labelMap = {};

  Object.entries(rawData).forEach(([rawKey, value]) => {
    const trimmed = rawKey !== undefined && rawKey !== null ? rawKey.toString().trim() : '';
    if (!trimmed) {
      return;
    }

    const candidate = sanitiseKeyForFirebase(trimmed);
    const safeKey = ensureUniqueKey(candidate, payload, 'field');
    payload[safeKey] = value;

    if (safeKey !== trimmed) {
      labelMap[safeKey] = trimmed;
    }
  });

  return {
    payload,
    labelMap
  };
};

const FormViewer = () => {
  const { userId, formId } = useParams();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [baseFields, setBaseFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [webhookUrl, setWebhookUrl] = useState('');
  const [formName, setFormName] = useState('Public Form');
  const [clientEmail, setClientEmail] = useState('');
  const [backupEmail, setBackupEmail] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('default');
  const [showReferenceNumber, setShowReferenceNumber] = useState(true);
  const [formType, setFormType] = useState('single');
  const [variants, setVariants] = useState([]);
  const [activeVariantId, setActiveVariantId] = useState(null);
  const [requireAllFields, setRequireAllFields] = useState(true);
  const [loading, setLoading] = useState(true);
  const [queryResults, setQueryResults] = useState({});
  const [queryLoading, setQueryLoading] = useState({});
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [storedPasswordHash, setStoredPasswordHash] = useState('');
  
  // PayFast payment settings
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [variantPaymentSettings, setVariantPaymentSettings] = useState({});
  
  const [planStatus, setPlanStatus] = useState({
    limitReached: false,
    planKey: DEFAULT_PLAN,
    planName: PLAN_CONFIG[DEFAULT_PLAN].name,
    limitLabel: formatLimitLabel(getPlanLimit(DEFAULT_PLAN)),
    unlimited: isUnlimitedLimit(getPlanLimit(DEFAULT_PLAN)),
    usageCount: 0,
    limitNumeric: getPlanLimit(DEFAULT_PLAN),
    usageMonth: new Date().toISOString().slice(0, 7)
  });
  const [planStatusLoading, setPlanStatusLoading] = useState(true);

  const normaliseVariants = (rawVariants) => {
    if (!rawVariants) {
      return [];
    }

    let variantList = [];

    if (Array.isArray(rawVariants)) {
      variantList = rawVariants;
    } else if (typeof rawVariants === 'object') {
      variantList = Object.entries(rawVariants).map(([key, value]) => ({ id: key, ...(value || {}) }));
    }

    return variantList.map((variant, index) => ({
      id: variant?.id || `variant-${index}-${Date.now()}`,
      name: variant?.name || `Option ${index + 1}`,
      fields: Array.isArray(variant?.fields) ? variant.fields : []
    }));
  };

  const generateRandomReference = () => Math.floor(100000000 + Math.random() * 900000000);

  const hashPassword = async (password) => {
    if (!window.crypto || !window.crypto.subtle) {
      return Array.from(password)
        .map(char => char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('');
    }
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  };

  // Function to query external APIs
  const queryAPI = async (fieldLabel, searchTerm, apiConfig) => {
    if (!searchTerm || searchTerm.length < 2 || !apiConfig?.url) return;

    setQueryLoading(prev => ({ ...prev, [fieldLabel]: true }));
    
    try {
      const response = await fetch(apiConfig.url + `?search=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...apiConfig.headers
        }
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      
      // Navigate to the data using the dataPath
      let results = data;
      if (apiConfig.dataPath) {
        const pathParts = apiConfig.dataPath.split('.');
        for (const part of pathParts) {
          results = results?.[part];
        }
      }

      // Ensure results is an array
      if (!Array.isArray(results)) {
        results = [];
      }

      // Filter results based on display field and search term
      const filteredResults = results.filter(item => {
        const displayValue = item[apiConfig.displayField] || '';
        return displayValue.toLowerCase().includes(searchTerm.toLowerCase());
      }).slice(0, 10); // Limit to 10 results

      setQueryResults(prev => ({ ...prev, [fieldLabel]: filteredResults }));
    } catch (error) {
      console.error('API query error:', error);
      setQueryResults(prev => ({ ...prev, [fieldLabel]: [] }));
    } finally {
      setQueryLoading(prev => ({ ...prev, [fieldLabel]: false }));
    }
  };

  const handleVariantChange = (selection) => {
    setActiveVariantId(selection);
    setFields(collectFieldsForSelection(selection, variants, baseFields));
    setFormData((prev) => ({
      'Form Reference Number': prev['Form Reference Number'] || generateRandomReference()
    }));
    setQueryResults({});
    setQueryLoading({});
  };

  useEffect(() => {
    const fetchForm = async () => {
      try {
        let formRef;
        
        console.log('FormViewer - userId:', userId);
        console.log('FormViewer - formId:', formId);
        
        // If formId is provided, use new multi-form structure
        if (formId) {
          const path = `users/${userId}/forms/${formId}`;
          console.log('FormViewer - Fetching from NEW path:', path);
          formRef = ref(realTimeDb, path);
        } else {
          const path = `users/${userId}/form`;
          console.log('FormViewer - Fetching from LEGACY path:', path);
          formRef = ref(realTimeDb, path);
        }
        
        const formSnapshot = await get(formRef);

        if (formSnapshot.exists()) {
          const data = formSnapshot.val();
          console.log('FormViewer - Form data found:', data);

          const detectedType = data.formType || 'single';
          setFormType(detectedType);

          const incomingBaseFields = Array.isArray(data.fields) ? data.fields : [];
          setBaseFields(incomingBaseFields);

          const normalisedVariants = normaliseVariants(data.variants);
          setVariants(normalisedVariants);

          if (detectedType === 'stackable') {
            const defaultSelection = normalisedVariants[0]?.id || 'all';
            setActiveVariantId(defaultSelection);
            setFields(collectFieldsForSelection(defaultSelection, normalisedVariants, incomingBaseFields));
          } else {
            setActiveVariantId(null);
            setFields(incomingBaseFields);
          }

          setWebhookUrl(data.webhookUrl || '');
          setFormName(data.title || data.formTitle || 'srsa mk2 flowbuilder 1st');
          setSelectedStyle(data.style || 'default');
          setClientEmail(data.clientEmail || data.email || '');
          setBackupEmail(data.backupEmail || '');
          setShowReferenceNumber(data.showReferenceNumber !== undefined ? data.showReferenceNumber : true);
          setRequireAllFields(data.requireAllFields !== undefined ? data.requireAllFields : true);
          
          // Load PayFast payment settings from variants (for stackable forms) or global (for single forms)
          const paymentSettingsMap = {};
          if (data.variants) {
            // Stackable form - load payment settings per variant
            Object.keys(data.variants).forEach(variantId => {
              const variant = data.variants[variantId];
              paymentSettingsMap[variantId] = {
                enabled: variant?.paymentEnabled || false,
                url: variant?.paymentUrl || ''
              };
            });
          } else {
            // Single form - use global payment settings
            paymentSettingsMap['global'] = {
              enabled: data.paymentEnabled || false,
              url: data.paymentUrl || ''
            };
          }
          setVariantPaymentSettings(paymentSettingsMap);
          
          // Set initial payment settings (will be updated when variant changes)
          setPaymentEnabled(data.paymentEnabled || false);
          setPaymentUrl(data.paymentUrl || '');
          
          const storedHash = data.accessPasswordHash || '';
          const isPublic = data.isPublic !== undefined ? data.isPublic : true;
          setStoredPasswordHash(storedHash);
          const needsPassword = !isPublic && Boolean(storedHash);
          setRequiresPassword(needsPassword);
          setAccessGranted(!needsPassword);
          setPasswordInput('');
          setPasswordError('');
          setFormData({
            'Form Reference Number': generateRandomReference()
          });
        } else {
          console.log('FormViewer - No form data found at path');
          alert('Form not found.');
          navigate('/');
        }
      } catch (error) {
        console.error('Error fetching form:', error);
        alert('Error fetching form details.');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [userId, formId, navigate]);

  useEffect(() => {
    const fetchPlanStatus = async () => {
      if (!userId) {
        return;
      }

      setPlanStatusLoading(true);

      try {
        const profileRef = ref(realTimeDb, `users/${userId}`);
        const snapshot = await get(profileRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          const planKey = PLAN_CONFIG[data.plan] ? data.plan : DEFAULT_PLAN;
          const planMeta = PLAN_CONFIG[planKey] || PLAN_CONFIG[DEFAULT_PLAN];
          const configuredLimit = data.monthlyLimit !== undefined ? data.monthlyLimit : getPlanLimit(planKey);
          const unlimited = isUnlimitedLimit(configuredLimit) || isUnlimitedLimit(planMeta.limit);
          const currentMonth = new Date().toISOString().slice(0, 7);
          let usageMonth = data.usage?.month || currentMonth;
          let usageCount = data.usage?.count ?? 0;
          const updates = {};

          if (usageMonth !== currentMonth) {
            usageMonth = currentMonth;
            usageCount = 0;
            updates['usage/month'] = usageMonth;
            updates['usage/count'] = usageCount;
          }

          if (Object.keys(updates).length > 0) {
            await update(profileRef, updates);
          }

          const limitNumeric = unlimited ? Number.POSITIVE_INFINITY : Math.max(Number(configuredLimit) || 0, 0);
          const limitReached = !unlimited && usageCount >= limitNumeric;

          setPlanStatus({
            limitReached,
            planKey,
            planName: planMeta.name,
            limitLabel: formatLimitLabel(configuredLimit),
            unlimited,
            usageCount,
            limitNumeric,
            usageMonth
          });
        } else {
          const fallbackLimit = getPlanLimit(DEFAULT_PLAN);
          setPlanStatus({
            limitReached: false,
            planKey: DEFAULT_PLAN,
            planName: PLAN_CONFIG[DEFAULT_PLAN].name,
            limitLabel: formatLimitLabel(fallbackLimit),
            unlimited: isUnlimitedLimit(fallbackLimit),
            usageCount: 0,
            limitNumeric: fallbackLimit,
            usageMonth: new Date().toISOString().slice(0, 7)
          });
        }
      } catch (error) {
        console.error('Error checking submission allowance for form:', error);
      } finally {
        setPlanStatusLoading(false);
      }
    };

    fetchPlanStatus();
  }, [userId]);

  useEffect(() => {
    const loadStyle = async () => {
      try {
        if (selectedStyle === 'default') {
          await import('./FormViewer.css');
        } else if (selectedStyle === 'style1') {
          await import('./FormViewer1.css');
        } else if (selectedStyle === 'style2') {
          await import('./FormViewer2.css');
        } else if (selectedStyle === 'style3') {
          await import('./FormViewer3.css');
        }
      } catch (error) {
        console.error('Error loading style:', error);
      }
    };

    loadStyle();
  }, [selectedStyle]);

  // Update PayFast settings when active variant changes
  useEffect(() => {
    if (formType === 'stackable' && activeVariantId) {
      // For stackable forms, use variant-specific payment settings
      const variantSettings = variantPaymentSettings[activeVariantId];
      if (variantSettings) {
        setPaymentEnabled(variantSettings.enabled);
        setPaymentUrl(variantSettings.url);
      }
    } else if (formType === 'single') {
      // For single forms, use global payment settings
      const globalSettings = variantPaymentSettings['global'];
      if (globalSettings) {
        setPaymentEnabled(globalSettings.enabled);
        setPaymentUrl(globalSettings.url);
      }
    }
  }, [activeVariantId, formType, variantPaymentSettings]);

  const normalizeDropdownOptions = (options = []) =>
    options
      .map((option) =>
        typeof option === 'string'
          ? { label: option, note: '' }
          : {
              label: option?.label || '',
              note: option?.note || ''
            }
      )
      .filter((option) => option.label);

  const handleChange = (e, fieldId) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldId]: e.target.value,
    }));
  };

  // Function to check if all required fields are filled
  const isFormValid = () => {
    if (!requireAllFields) {
      return true;
    }

    if (!fields.length) {
      return true;
    }

    const requiredFields = fields.filter((field) => field.label !== 'Form Reference Number');

    if (!requiredFields.length) {
      return true;
    }

    return requiredFields.every((field) => {
      const value = formData[field.label];
      return value !== undefined && value !== null && value.toString().trim() !== '';
    });
  };

  // Function to save submission to database
  const saveSubmissionToDatabase = async (submissionData) => {
    try {
      const userRef = ref(realTimeDb, `users/${userId}`);
      const userSnapshot = await get(userRef);

      if (!userSnapshot.exists()) {
        throw new Error('User profile not found');
      }

      const userData = userSnapshot.val();
      const planKey = PLAN_CONFIG[userData.plan] ? userData.plan : DEFAULT_PLAN;
      const configuredLimit = userData.monthlyLimit !== undefined ? userData.monthlyLimit : getPlanLimit(planKey);
      const unlimited = isUnlimitedLimit(configuredLimit) || isUnlimitedLimit(getPlanLimit(planKey));
      const limitNumeric = unlimited ? Number.POSITIVE_INFINITY : Math.max(Number(configuredLimit ?? getPlanLimit(planKey) ?? 0), 0);
      const usageRef = ref(realTimeDb, `users/${userId}/usage`);
      const currentMonth = new Date().toISOString().slice(0, 7);
      let limitReached = false;

      const transactionResult = await runTransaction(usageRef, (usage) => {
        const usageData = usage || {};
        let month = usageData.month || currentMonth;
        let count = usageData.count || 0;

        if (month !== currentMonth) {
          month = currentMonth;
          count = 0;
        }

        if (!unlimited && count + 1 > limitNumeric) {
          limitReached = true;
          return usageData;
        }

        return {
          month,
          count: count + 1
        };
      });

      if (!transactionResult.committed || limitReached) {
        return {
          success: false,
          reason: 'limit',
          planKey,
          limitLabel: formatLimitLabel(configuredLimit ?? getPlanLimit(planKey)),
          unlimited
        };
      }

      const submissionsRef = ref(realTimeDb, `users/${userId}/submissions`);
      const { payload, labelMap } = buildSanitisedSubmissionPayload(submissionData);
      const submissionRecord = {
        ...payload,
        ...(Object.keys(labelMap).length ? { [LABEL_MAP_KEY]: labelMap } : {}),
        submittedAt: new Date().toISOString(),
        formTitle: formName,
        formId: formId || 'legacy'
      };
      
      await push(submissionsRef, submissionRecord);
      
      // Also save to form-specific submissions if formId exists
      if (formId) {
  const formSubmissionsRef = ref(realTimeDb, `users/${userId}/forms/${formId}/submissions`);
  await push(formSubmissionsRef, submissionRecord);
      }
      
      console.log('Submission saved to database successfully');
      return { success: true };
    } catch (error) {
      console.error('Error saving submission to database:', error);
      return { success: false, reason: 'error' };
    }
  };

  const sendBackupEmail = async (email, formData, formTitle) => {
    const formattedHtml = Object.entries(formData)
      .map(([key, value]) => `<p><strong>${key}:</strong> ${value}</p>`)
      .join('');

    try {
      const response = await fetch("https://api.resend.com/emails", {
        method: "POST",
        headers: {
          "Authorization": "Bearer YOUR_RESEND_API_KEY", // 🔁 Replace with your API key
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          from: "noreply@yourdomain.com", // 🔁 Replace with your verified sender
          to: email,
          subject: `Form submission: ${formTitle}`,
          html: `<h3>${formTitle}</h3>${formattedHtml}`,
        }),
      });

      if (!response.ok) {
        console.warn("Backup email failed:", await response.text());
      } else {
        console.log("Backup email sent to", email);
      }
    } catch (err) {
      console.error("Email error:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Double-check validation before submission
    if (!isFormValid()) {
      alert('Please fill in all required fields before submitting.');
      return;
    }

    const referenceNumber = formData['Form Reference Number'] || generateRandomReference();
    const selectedVariantMeta = formType === 'stackable'
      ? (
          activeVariantId === 'all'
            ? { id: 'all', name: 'All Subforms Combined' }
            : variants.find((variant) => variant.id === activeVariantId) || { id: activeVariantId, name: 'Selected Subform' }
        )
      : null;
    const fullData = {
      ...formData,
      'Form Reference Number': referenceNumber,
      ...(selectedVariantMeta
        ? {
            'Selected Subform': selectedVariantMeta.name,
            'Selected Subform ID': selectedVariantMeta.id
          }
        : {})
    };

    // Save submission to database
    const saveResult = await saveSubmissionToDatabase(fullData);

    if (!saveResult.success) {
      if (saveResult.reason === 'limit') {
        const planMeta = PLAN_CONFIG[saveResult.planKey] || PLAN_CONFIG[DEFAULT_PLAN];
        const limitMessage = saveResult.unlimited ? 'unlimited submissions' : `${saveResult.limitLabel} submissions`;
        alert(`Submission limit reached for your ${planMeta.name} plan (${limitMessage}). Please upgrade your plan in the dashboard to continue.`);
        return;
      }
      alert('We could not save your submission. Please try again later.');
      return;
    }

    // Check if PayFast payment is enabled for this form
    if (paymentEnabled && paymentUrl && paymentUrl.trim()) {
      // Save form data first
      const timestamp = Date.now();
      const submissionData = {
        formId: formId || 'default',
        data: fullData,
        timestamp,
        submittedAt: new Date().toISOString(),
      };

      if (userId) {
        await set(ref(realTimeDb, `form-submissions/${userId}/${submissionData.formId}/${timestamp}`), submissionData);
      } else {
        await set(ref(realTimeDb, `form-submissions/anonymous/${submissionData.formId}/${timestamp}`), submissionData);
      }

      // Redirect to payment URL
      window.location.href = paymentUrl;
      return;
    }

    if (clientEmail) {
      console.log('Client email placeholder — add logic here if needed.');
    }

    if (backupEmail) {
      await sendBackupEmail(backupEmail, fullData, formName);
    }

    if (webhookUrl) {
      try {
        const urlEncodedData = new URLSearchParams(fullData).toString();

        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: urlEncodedData,
        });

        if (response.ok) {
          alert('Form submitted successfully!');
          navigate('/thank-you');
        } else {
          console.error('Webhook submission failed:', response.status, response.statusText);
          alert('Error submitting to webhook.');
        }
      } catch (error) {
        console.error('Error submitting to webhook:', error);
        alert('Error submitting to webhook.');
      }
    } else {
      alert('Form submitted successfully!');
      navigate('/thank-you');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!passwordInput.trim()) {
      setPasswordError('Please enter the password.');
      return;
    }

    try {
      const hashedInput = await hashPassword(passwordInput.trim());
      if (hashedInput === storedPasswordHash) {
        setAccessGranted(true);
        setPasswordInput('');
        setPasswordError('');
      } else {
        setPasswordError('Incorrect password. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      setPasswordError('Something went wrong. Please try again.');
    }
  };

  // Query Field Component
  const QueryField = ({ field, value, onChange, isRequired }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [showDropdown, setShowDropdown] = useState(false);
    const results = queryResults[field.label] || [];
    const loading = queryLoading[field.label] || false;

    const handleInputChange = (e) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onChange(e, field.label);
      
      // Trigger API query with debounce
      clearTimeout(window.queryTimeout);
      window.queryTimeout = setTimeout(() => {
        queryAPI(field.label, newValue, field.apiConfig);
        setShowDropdown(newValue.length >= 2);
      }, 300);
    };

    const selectItem = (item) => {
      const displayValue = item[field.apiConfig.displayField] || '';
      const actualValue = field.apiConfig.valueField ? item[field.apiConfig.valueField] : displayValue;
      
      setInputValue(displayValue);
      setShowDropdown(false);
      
      // Create a synthetic event for the onChange handler
      const syntheticEvent = {
        target: {
          value: actualValue
        }
      };
      onChange(syntheticEvent, field.label);
    };

    return (
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={`Type to search ${field.label.toLowerCase()}...`}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
          required={isRequired}
          onFocus={() => inputValue.length >= 2 && setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        />
        
        {loading && (
          <div style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '12px',
            color: '#666'
          }}>
            🔄 Searching...
          </div>
        )}
        
        {showDropdown && results.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            {results.map((item, index) => (
              <div
                key={index}
                onClick={() => selectItem(item)}
                style={{
                  padding: '10px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                <div style={{ fontWeight: 'bold' }}>
                  {item[field.apiConfig.displayField]}
                </div>
                {field.apiConfig.valueField && item[field.apiConfig.valueField] && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    ID: {item[field.apiConfig.valueField]}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading || planStatusLoading) return <p>Loading form...</p>;

  if (requiresPassword && !accessGranted) {
    return (
      <div className="form-viewer-container">
        <div className="form-content" style={{ maxWidth: '420px' }}>
          <h2>{formName}</h2>
          <p style={{ color: '#4a5568', marginBottom: '20px' }}>
            This form is password protected. Enter the password provided to you to continue.
          </p>
          <form onSubmit={handlePasswordSubmit}>
            <div className="form-field">
              <label>Access Password</label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>
            {passwordError && (
              <p style={{ color: '#e53e3e', marginBottom: '10px' }}>{passwordError}</p>
            )}
            <button type="submit">Unlock Form</button>
          </form>
        </div>
      </div>
    );
  }

  if (planStatus.limitReached && !planStatus.unlimited) {
    return (
      <div className="form-viewer-container">
        <div className="form-content" style={{ maxWidth: '460px', textAlign: 'center' }}>
          <h2>Submissions are paused</h2>
          <p style={{ color: '#4a5568', marginBottom: '16px' }}>
            This form has reached the {planStatus.limitLabel} monthly submission allowance included with the {planStatus.planName} plan.
          </p>
          <p style={{ color: '#4a5568', marginBottom: '20px' }}>
            Please check back after the next monthly reset or contact the form owner if this is unexpected.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-viewer-container">
      <div className="form-content">
        <h2>{formName}</h2>
        <form onSubmit={handleSubmit}>
          {showReferenceNumber && (
            <div className="form-field">
              <label>Form Reference Number</label>
              <input type="text" value={formData['Form Reference Number']} readOnly />
            </div>
          )}
          {formType === 'stackable' && variants.length > 0 && (
            <div className="form-field">
              <label>Select Subform</label>
              <select
                value={activeVariantId || 'all'}
                onChange={(e) => handleVariantChange(e.target.value)}
              >
                {variants.map((variant) => (
                  <option key={variant.id} value={variant.id}>{variant.name}</option>
                ))}
                <option value="all">All Subforms Combined</option>
              </select>
              <p style={{fontSize: '12px', color: '#64748b', marginTop: '6px'}}>
                Choose a subform to complete, or pick All Subforms Combined to fill every question once.
              </p>
            </div>
          )}
          {fields.map((field, index) => (
            <div key={index} className="form-field">
              <label>
                {field.label}
                {requireAllFields ? (
                  <span style={{ marginLeft: '6px', color: '#ef4444' }}>*</span>
                ) : (
                  <span style={{ marginLeft: '6px', fontSize: '13px', color: '#64748b' }}>(Optional)</span>
                )}
              </label>
              {field.type === 'dropdown' ? (
                (() => {
                  const options = normalizeDropdownOptions(field.options);
                  const selectedValue = formData[field.label] || '';
                  const selectedNote = options.find((option) => option.label === selectedValue)?.note;

                  return (
                    <>
                      <select
                        onChange={(e) => handleChange(e, field.label)}
                        required={requireAllFields}
                        value={selectedValue}
                      >
                        <option value="">Select an option</option>
                        {options.map((option, idx) => (
                          <option key={idx} value={option.label}>{option.label}</option>
                        ))}
                      </select>
                      {selectedNote && (
                        <div className="dropdown-note">
                          {selectedNote}
                        </div>
                      )}
                    </>
                  );
                })()
              ) : field.type === 'query' ? (
                <QueryField
                  field={field}
                  value={formData[field.label] || ''}
                  onChange={handleChange}
                  isRequired={requireAllFields}
                />
              ) : (
                <input
                  type={field.type || 'text'}
                  value={formData[field.label] || ''}
                  onChange={(e) => handleChange(e, field.label)}
                  placeholder={field.label}
                  required={requireAllFields}
                />
              )}
            </div>
          ))}
          <button type="submit" disabled={!isFormValid()}>
            {paymentEnabled && paymentUrl ? 'Pay Now' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FormViewer;
